import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut, Calendar, FileText, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Font mapping for logo styles
const fontStyles = {
  'playfair': '"Playfair Display", serif',
  'cormorant': '"Cormorant Garamond", serif',
  'baskerville': '"Libre Baskerville", serif',
  'cinzel': '"Cinzel", serif',
  'bodoni': '"Bodoni Moda", serif'
};

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoFontStyle, setLogoFontStyle] = useState('playfair');
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/cms/site-settings`);
      if (response.data.logo_font_style) {
        setLogoFontStyle(response.data.logo_font_style);
      }
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/services/psychotherapy', label: 'Psychotherapy' },
    { href: '/services/trauma', label: 'Trauma' },
    { href: '/services/couples', label: 'Couples' },
    { href: '/services/bereavement', label: 'Bereavement' },
    { href: '/discovery', label: 'Discovery' },
    { href: '/about', label: 'About' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/40">
      {/* Google Fonts for logo */}
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600&family=Cormorant+Garamond:wght@400;500;600&family=Libre+Baskerville:wght@400;700&family=Cinzel:wght@400;500;600&family=Bodoni+Moda:wght@400;500;600&display=swap" rel="stylesheet" />
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="nav-logo">
            <span 
              className="text-2xl font-semibold text-primary tracking-wider"
              style={{ fontFamily: fontStyles[logoFontStyle] || fontStyles.playfair }}
            >
              KBC
            </span>
            <span className="font-heading text-lg text-muted-foreground hidden sm:inline">Psychotherapy</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-300 ${
                  isActive(link.href)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => navigate('/book')}
                  data-testid="nav-book-now-btn"
                >
                  Book Now
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="rounded-full" data-testid="nav-user-menu">
                      <User className="h-5 w-5 mr-2" />
                      {user?.first_name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-link">
                      <Calendar className="mr-2 h-4 w-4" />
                      My Appointments
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/intake-form')} data-testid="nav-intake-link">
                      <FileText className="mr-2 h-4 w-4" />
                      Intake Form
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-link">
                          <Settings className="mr-2 h-4 w-4" />
                          Admin Dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-btn">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="rounded-full"
                  onClick={() => navigate('/login')}
                  data-testid="nav-login-btn"
                >
                  Login
                </Button>
                <Button
                  className="rounded-full bg-primary hover:bg-primary/90"
                  onClick={() => navigate('/book')}
                  data-testid="nav-book-btn"
                >
                  Book Now
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-border/40"
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block py-2 text-sm font-medium ${
                    isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-border/40 space-y-2">
                {isAuthenticated ? (
                  <>
                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    >
                      My Dashboard
                    </Button>
                    <Button
                      className="w-full rounded-full"
                      onClick={() => { navigate('/book'); setMobileMenuOpen(false); }}
                    >
                      Book Now
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full rounded-full"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="w-full rounded-full"
                      onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    >
                      Login
                    </Button>
                    <Button
                      className="w-full rounded-full"
                      onClick={() => { navigate('/book'); setMobileMenuOpen(false); }}
                    >
                      Book Now
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
